import { useQueries } from "@tanstack/react-query";

import { PageLoadError } from "../components/PageLoadError";
import { useAuth } from "../contexts/useAuth";
import { GetOrganisationDetailsFn } from "../hooks/useOrganisation";
import { LabService } from "../services/labService";
import { AdminLabsPage } from "./AdminLabsPage";

export const ConnectedAdminLabsPage = () => {
  const { user } = useAuth();
  const organisationId = user?.organisation_id;

  const [adminLabs, organisation] = useQueries({
    queries: [
      {
        queryKey: ["admin-labs", organisationId],
        queryFn: async () => {
          return await LabService.getAdminLabDirectory();
        },
        enabled: Boolean(organisationId),
        retryOnMount: true,
        refetchOnMount: "always",
        refetchOnReconnect: true,
      },
      {
        queryKey: ["organisation", organisationId],
        queryFn: async () => {
          if (!organisationId) {
            throw new Error("Organisation ID is required");
          }
          return await GetOrganisationDetailsFn(organisationId);
        },
        enabled: Boolean(organisationId),
        retryOnMount: true,
        refetchOnMount: "always",
        refetchOnReconnect: true,
      },
    ],
  });

  const retryPageQueries = async () => {
    await Promise.all([adminLabs.refetch(), organisation.refetch()]);
  };

  if (!user || !organisationId) {
    return null;
  }

  if (adminLabs.isLoading || organisation.isLoading) {
    return <div className="skeleton h-4 w-full"></div>;
  }

  if (adminLabs.isError) {
    return (
      <PageLoadError
        message="Error loading labs"
        onRetry={retryPageQueries}
        isRetrying={adminLabs.isFetching || organisation.isFetching}
      />
    );
  }

  if (organisation.isError) {
    return (
      <PageLoadError
        message="Error loading organisation data"
        onRetry={retryPageQueries}
        isRetrying={adminLabs.isFetching || organisation.isFetching}
      />
    );
  }

  if (!adminLabs.data || !organisation.data) {
    return (
      <PageLoadError
        message="Labs data not available"
        onRetry={retryPageQueries}
        isRetrying={adminLabs.isFetching || organisation.isFetching}
      />
    );
  }

  return (
    <AdminLabsPage
      directory={adminLabs.data}
      organisation={organisation.data}
    />
  );
};